import { useTranslation } from 'react-i18next'

const LEVELS = [
  { level: 1, emoji: '🌱', xp: 0 },
  { level: 2, emoji: '📦', xp: 100 },
  { level: 3, emoji: '🔍', xp: 300 },
  { level: 4, emoji: '⭐', xp: 700 },
  { level: 5, emoji: '🏆', xp: 1500 },
]

interface Props {
  currentLevel: number
  currentXp: number
  nextLevelXp: number | null
  currentLevelXp: number
}

export default function LevelRoadmap({ currentLevel, currentXp, nextLevelXp, currentLevelXp }: Props) {
  const { t } = useTranslation('gamification')
  const xpInLevel = currentXp - currentLevelXp
  const xpNeeded = nextLevelXp ? nextLevelXp - currentLevelXp : 0
  const xpProgress = xpNeeded > 0 ? Math.min(xpInLevel / xpNeeded, 1) : 1

  return (
    <div className="rounded-2xl p-4" style={{ background: 'var(--app-card-bg)' }}>
      <p className="text-sm font-semibold mb-4" style={{ color: 'var(--tg-theme-text-color)' }}>
        {t('levelRoadmap')}
      </p>
      <div className="space-y-3">
        {LEVELS.map((lvl) => {
          const isAchieved = currentLevel >= lvl.level
          const isCurrent = currentLevel === lvl.level

          return (
            <div
              key={lvl.level}
              className="flex items-start gap-3 rounded-xl p-3"
              style={{
                background: isCurrent ? 'var(--app-page-bg)' : 'transparent',
                border: isCurrent ? '1.5px solid var(--tg-theme-button-color)' : '1.5px solid transparent',
              }}
            >
              {/* Level circle */}
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0"
                style={{
                  background: isAchieved ? 'var(--tg-theme-button-color)' : 'var(--app-page-bg)',
                  opacity: isAchieved ? 1 : 0.5,
                }}
              >
                {lvl.emoji}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <p
                    className="font-semibold text-sm"
                    style={{ color: 'var(--tg-theme-text-color)', opacity: isAchieved ? 1 : 0.5 }}
                  >
                    {t(`levels.${lvl.level}.name`)}
                  </p>
                  <p
                    className="text-xs opacity-40"
                    style={{ color: 'var(--tg-theme-text-color)' }}
                  >
                    {lvl.xp} XP
                  </p>
                </div>
                <p
                  className="text-xs mt-0.5"
                  style={{ color: 'var(--tg-theme-text-color)', opacity: isAchieved ? 0.7 : 0.35 }}
                >
                  {t(`levels.${lvl.level}.perk`)}
                </p>

                {/* XP progress bar for current level */}
                {isCurrent && nextLevelXp != null && (
                  <div className="mt-2">
                    <div
                      className="w-full h-1.5 rounded-full overflow-hidden"
                      style={{ background: 'var(--app-card-bg)' }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${xpProgress * 100}%`,
                          background: 'linear-gradient(90deg, var(--tg-theme-button-color), #6366f1)',
                        }}
                      />
                    </div>
                    <p className="text-[10px] mt-1 opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
                      {t('xpToNextLevel', { current: xpInLevel, needed: xpNeeded })}
                    </p>
                  </div>
                )}
                {isCurrent && nextLevelXp == null && (
                  <p className="text-xs mt-1 font-medium" style={{ color: '#f59e0b' }}>
                    {t('maxLevel')}
                  </p>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
